<script>
  import CancelCircleIcon from '@/assets/images/svg/icons/cancel-circle-white.svg';
  import RoundTimeIcon from '@/assets/images/svg/icons/round-time.svg';
  import PlayersMaxIcon from '@/assets/images/svg/icons/players-max.svg';
  import { GameLauncher } from '@/mixins/game-launcher';
  import { DialogHideScroll } from '@/mixins/dialogHideScroll';
  import { roomsConfig } from '@/data/games';
  import breakpoints from '@/mixins/breakpoints';

  const gameDescriptions = {
    dragonstone:
      'Compete in 90-seconds Player vs Player action to win the entire prize pot. Slay the dragon and double up your score.',

    'mission-amazon':
      'Take down the entire prize pot every 90 seconds in Player vs Player rounds. Defeat the legendary bosses and win big rewards.',

    'sector-x':
      'Compete in 60-second Player vs Player rounds to claim the entire prize pot. Defeat one of the four bosses to increase your score.',

    plinko:
      'Up to 10 players compete for the prize pool, each starting with 3 chips to place on the board. The highest total score at the end of the round wins the entire pot.',

    maxblast:
      'Experience the galactic thrill in Max Blast Champs! Outlast opponents leaping from the rocket to claim the full prize pot!',

    'multi-mines':
      'Select squares in five rounds, find hidden gems, hit the multipliers and stay clear of the mines. Achieve the highest score to win the prize pot.',

    'roll-or-hold':
      'A thrilling game of chance where players must decide between rolling the dice or holding their winnings.',
    'escape-the-circle':
      'An engaging game where players race against each other to guide a ball through a single exit opening, using strategy and timing to control the path.',

    'rolling-21':
      'Each player has 3 dice rolls to get to 21 without going over. The player with the highest score wins the Prize pot.',

    'scarab-plinko':
      'Up to 10 players compete for the prize pool, each starting with 3 chips. You can hit scarabs for multipliers and use bumps to steer your balls. The player with the highest score at the end wins the prize.',

    'max-blocks':
      'Find more treasures than your opponents and claim the prize! The numbers indicate how far away the valuable metals are. Up to 4 players can mine at the same time!',
    maxplosion:
      'Compete in matches where the last one standing wins. Use bombs to destroy walls, trap, and eliminate opponents. Collect power-ups to boost speed, bomb capacity, and blast radius.',

    'duel-21':
      'A fast-paced card game combining Blackjack and Solitaire, where up to 50 players compete simultaneously to build stacks totaling 21 without busting',

    'escape-the-circle-xtreme':
      'An exciting multiplayer game where up to 8 players race to guide their ball through a single exit, using strategy and timing to control the path.',
  };

  export default {
    name: 'RoomBuyInPopup',

    mixins: [GameLauncher, DialogHideScroll, breakpoints],

    components: {
      CancelCircleIcon,
      RoundTimeIcon,
      PlayersMaxIcon,
    },

    data: () => ({
      gameRequestIsPending: false,
    }),

    computed: {
      gameType() {
        return this.$store.state.dialog.roomBuyInGameType;
      },

      showPopup() {
        return this.gameType !== null;
      },

      description() {
        return gameDescriptions[this.gameType] || '';
      },

      playerMode() {
        return this.$store.state.playermode;
      },

      isQcMode() {
        return this.$store.getters.isQCMode;
      },

      roomsConfig() {
        return roomsConfig[this.playerMode]?.[this.gameType] || [];
      },

      currency() {
        return this.isQcMode ? 'QC' : 'USD';
      },

      games: () => ({
        plinko: {
          gameId: 1,
          homeUrlId: 'MLTP',
          paramsType: 'extended',
        },
        maxblast: {
          gameId: 864,
          homeUrlId: 'MBC',
        },
        'sector-x': {
          gameId: 867,
          homeUrlId: 'BTG',
        },
        'mission-amazon': {
          gameId: 862,
          homeUrlId: 'BTG',
        },
        'roll-or-hold': {
          gameId: 2,
          homeUrlId: 'BTG',
          paramsType: 'extended',
        },
        dragonstone: {
          gameId: 856,
          homeUrlId: 'BTG',
        },
        'rolling-21': {
          gameId: 5,
          homeUrlId: 'BTG',
          paramsType: 'extended',
        },
        'multi-mines': {
          gameId: 6,
          homeUrlId: 'BTG',
          paramsType: 'extended',
        },
        'escape-the-circle': {
          gameId: 10,
          homeUrlId: 'BTG',
          paramsType: 'extended',
        },
        'scarab-plinko': {
          gameId: 12,
          homeUrlId: 'BTG',
          paramsType: 'extended',
        },
        'max-blocks': {
          gameId: 14,
          homeUrlId: 'BTG',
          paramsType: 'extended',
        },
        maxplosion: {
          gameId: 15,
          homeUrlId: 'BTG',
          paramsType: 'extended',
        },
        'duel-21': {
          gameId: 16,
          homeUrlId: 'BTG',
          paramsType: 'extended',
        },
        'escape-the-circle-xtreme': {
          gameId: 18,
          homeUrlId: 'BTG',
          paramsType: 'extended',
        },
      }),

      currentGame() {
        return this.games[this.gameType];
      },

      assetsPath() {
        return '/images/game-lobby';
      },

      logoUrl() {
        return `${this.assetsPath}/logos/${this.gameType}-${this.description !== '' ? 'sm' : 'lg'}.webp?v=6`;
      },

      bgUrl() {
        return `${this.assetsPath}/backgrounds/${this.gameType}-sm.webp?v=6`;
      },
    },

    watch: {
      async showPopup() {
        await this.$nextTick();
        const content = document.querySelector('.v-dialog__content--active');
        content?.classList.toggle('room-buy-in-content');
      },
    },

    methods: {
      formatBalance: (value = '') => value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','),

      closePopup() {
        this.$emit('close');
        this.$store.commit('SET_ROOM_BUYIN_POPUP_DATA', null);
      },

      getMaxPrizeUrl(maxPrize) {
        if (this.isQcMode) return `/images/game-lobby/payouts/${maxPrize}-QC.webp?v=2`;
        return `/images/game-lobby/payouts/$${maxPrize}.webp?v=1`;
      },

      async onPlayClick(buyInAmount) {
        if (!this.currentGame) return;

        const { gameId, homeUrlId, paramsType } = this.currentGame;
        try {
          this.gameRequestIsPending = true;
          await this.launchGame(gameId, homeUrlId, paramsType, buyInAmount * 100);
          this.closePopup();
        } catch (error) {
          console.error(error);
        } finally {
          this.gameRequestIsPending = false;
        }
      },
    },
  };
</script>

<template>
  <v-dialog
    v-if="showPopup"
    v-model="showPopup"
    :fullscreen="$vuetify.breakpoint.width <= 472"
    content-class="room-buyin-dialog no-shadow"
    max-width="472px"
    @click:outside="closePopup"
    @keydown.esc="closePopup"
  >
    <div
      class="wrapper"
      :style="{ '--cards': roomsConfig.length, '--offset': this.playerMode === 'MQC' ? '40px' : '0px' }"
      :class="{ fullscreen: $vuetify.breakpoint.width <= 472 }"
    >
      <CancelCircleIcon class="close-button" @click="closePopup" />

      <div class="content">
        <v-img class="popup-header" :alt="`${gameType} lobby background`" :src="bgUrl" width="100%" height="auto" cover>
          <img :alt="`${gameType} logo`" :src="logoUrl" class="game-logo" :class="`game-logo--${gameType}`" />
          <div v-if="description" class="game-desc" :class="`game-desc--${gameType}`">
            {{ description }}
          </div>
        </v-img>

        <div class="rooms" :class="{ 'rooms--short': roomsConfig.length <= 3 && isDesktop }">
          <div class="room" v-for="room in roomsConfig" :key="`room-${room.buyInAmount}`">
            <img
              :src="getMaxPrizeUrl(room.maxPrize)"
              :alt="`You can win up to $${room.maxPrize}`"
              width="114"
              height="114"
            />
            <div class="room-limits">
              <div class="frame">
                <div class="icon-wrapper">
                  <PlayersMaxIcon />
                  <div class="icon-text">{{ room.playersMax }}</div>
                </div>
                <div class="frame-text">Players Max</div>
              </div>
              <div class="frame">
                <div class="icon-wrapper">
                  <RoundTimeIcon />
                  <div class="icon-text icon-text--time">{{ room.roundTime }}</div>
                </div>
                <div class="frame-text">Round Time</div>
              </div>
            </div>
            <div class="room-buyin">
              <div class="buyin-text">
                <span>Buy-in</span>
                <br />
                <b>{{ formatBalance(room.buyInAmount) }} {{ currency }}</b>
              </div>
              <button
                class="play-button"
                :disabled="gameRequestIsPending"
                :aria-disabled="gameRequestIsPending.toString()"
                aria-label="Play the game"
                @click.stop="onPlayClick(room.buyInAmount)"
              >
                <span>PLAY</span>
              </button>
            </div>
          </div>
        </div>

        <div v-if="isQcMode" class="qc-warning">
          Questcoin (QC) cannot be converted to real money and can only be used for free gameplay.
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<style lang="scss" scoped>
  .wrapper {
    container: buyin-dialog / size;
    width: 100%;
    max-height: 100%;
    padding: 40px 12px;
    border-radius: 10px;
    background: linear-gradient(180deg, #350f53 0%, #24073d 100%);

    &:not(.fullscreen) {
      height: calc(340px + var(--offset) + 120px * var(--cards));
    }

    &.fullscreen {
      height: 100%;
    }

    @media screen and (orientation: landscape) and (max-width: 1024px) {
      min-height: 448px;
    }
  }

  .qc-warning {
    margin-top: 32px;
    padding-bottom: 60px;
    padding-inline: 14px;
    text-align: center;
    color: #fff;
    font-size: 16px;
    font-weight: 400;
    line-height: 150%;
  }

  @container buyin-dialog (width > 0px) {
    .content {
      width: 100%;
      height: 100%;
      max-width: 392px;
      margin-inline: auto;
      border-radius: 10px;
    }

    .close-button {
      position: absolute;
      top: 8px;
      right: 8px;
      width: 24px;
      height: 24px;
      cursor: pointer;
    }

    .popup-header {
      height: auto;
      aspect-ratio: 49 / 25;

      &::v-deep .v-responsive__content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding-top: 10px;
        padding-bottom: 10px;
      }
    }

    .game-logo {
      position: relative;
      display: block;
      margin: 0 auto;
      width: 100%;

      &--scarab-plinko {
        margin-bottom: -12px;
      }
    }

    .game-desc {
      color: #fff;
      text-align: center;
      font-size: 15px;
      font-weight: 400;
      line-height: 115%;
      margin-inline: 14px;

      @media screen and (max-width: 420px) {
        font-size: 13px;
      }

      &--max-blocks {
        text-shadow:
          0 2px 4px #000,
          0 2px 4px #000,
          0 2px 4px #000,
          0 2px 4px #000;
      }
    }

    .rooms {
      display: flex;
      flex-direction: column;
      gap: 16px;
      margin-top: 24px;
      padding-right: 10px;
      width: calc(100% + 10px);
      max-height: calc(100cqh - 224px);
      overflow-y: auto;

      &--short {
        padding-right: 0;
        width: 100%;
      }

      &::-webkit-scrollbar-track {
        background: #1c0630 !important;
      }

      &::-webkit-scrollbar-thumb {
        background-color: #69279e !important;
      }

      @-moz-document url-prefix() {
        scrollbar-width: thin;
        scrollbar-color: #69279e #1c0630;
      }
    }

    .room {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 3px;
      height: 120px;
      border-radius: 10px;
      border: 3px solid #fff;
      background: linear-gradient(0deg, #eae8e1 0%, #f4f2eb 100%);
    }

    .room-limits {
      display: flex;
      flex-direction: column;
      gap: 6px;

      .frame {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 130px;
        height: 50px;
        padding: 4px 10px;
        border-radius: 10px;
        border: 1px solid rgba(0, 0, 0, 0.1);
      }

      .icon-wrapper {
        position: relative;
        width: 40px;
        height: 40px;
      }

      .icon-text {
        position: absolute;
        bottom: 1px;
        width: 100%;
        text-align: center;
        font-size: 14px;
        font-weight: 600;
        line-height: 150%;
        letter-spacing: -0.5px;
        color: #efeee6;

        &--time {
          left: -1px;
          bottom: 10px;
          color: #1c0630;
          font-size: 15px;
        }
      }

      .frame-text {
        color: rgba(28, 6, 48, 0.75);
        font-size: 12px;
        font-weight: 600;
        line-height: 150%;
        flex-shrink: 0;
      }
    }

    .room-buyin {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 9px;
      max-width: 100px;
      margin-right: 3px;

      .buyin-text {
        text-align: center;
        font-size: 12px;
        font-weight: 600;
        line-height: 150%;

        span {
          color: rgba(28, 6, 48, 0.75);
        }

        b {
          color: #1c0630;
          text-align: center;
          font-size: 20px;
          font-weight: 900;
          line-height: 100%;
        }
      }

      .play-button {
        display: flex;
        width: 100%;
        height: 40px;
        padding: 14px;
        justify-content: center;
        align-items: center;
        gap: 4px;
        flex-shrink: 0;
        border-radius: 10px;
        background: #17a34a;
        cursor: pointer;

        &:hover {
          filter: brightness(103%);
        }

        &:active {
          filter: brightness(97%);
        }

        &[disabled] {
          pointer-events: none;
          filter: grayscale(1);
        }

        span {
          color: #fff;
          text-align: center;
          font-size: 15px;
          font-weight: 800;
          line-height: 100%;
          letter-spacing: 0.15px;
        }
      }
    }
  }

  @container buyin-dialog (width > 371px) {
    .room {
      gap: 6px;
    }

    .room-buyin .play-button {
      padding: 14px 28px;
    }
  }
</style>

<style lang="scss">
  .v-dialog__content--active.room-buy-in-content {
    overflow-y: hidden;
  }

  .room-buyin-dialog {
    position: relative;
    overflow-y: hidden;
    height: 100%;

    @media screen and (orientation: landscape) and (max-width: 1024px) {
      overflow-y: auto;
    }
  }
</style>
